import type {
  BankTransferPaymentMethodDetails,
  BillingPaymentMethodInfo,
  PaymentMethodInfo,
} from '@/components/Invoice/types';

function optionalStr(record: Record<string, unknown>, snakeKey: string, camelKey: string): string | undefined {
  const value = record[snakeKey] ?? record[camelKey];
  if (value === undefined || value === null) return undefined;
  return String(value);
}

function normalizeBankTransfer(raw: Record<string, unknown>): BankTransferPaymentMethodDetails {
  return {
    accountCategory: optionalStr(raw, 'account_category', 'accountCategory'),
    accountName: optionalStr(raw, 'account_name', 'accountName'),
    accountNumber: optionalStr(raw, 'account_number', 'accountNumber'),
    bankName: optionalStr(raw, 'bank_name', 'bankName'),
    gaId: optionalStr(raw, 'ga_id', 'gaId'),
    gaOrderId: optionalStr(raw, 'ga_order_id', 'gaOrderId'),
    referenceCode: optionalStr(raw, 'reference_code', 'referenceCode'),
    routingNumber: optionalStr(raw, 'routing_number', 'routingNumber'),
    swiftCode: optionalStr(raw, 'swift_code', 'swiftCode'),
  };
}

function bankTransferHasValues(details: BankTransferPaymentMethodDetails): boolean {
  return Object.values(details).some((v) => v !== undefined && v !== '');
}

/**
 * Maps invoice `paymentMethodInfo` from API snake_case (or mixed) to camelCase `PaymentMethodInfo`.
 */
export function normalizePaymentMethodInfo(raw: unknown): PaymentMethodInfo | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const root = raw as Record<string, unknown>;
  const billingRaw = (root.billing_payment ?? root.billingPayment) as Record<string, unknown> | undefined;
  if (!billingRaw) return undefined;

  const btRaw = (billingRaw.bank_transfer ?? billingRaw.bankTransfer) as Record<string, unknown> | undefined;
  const linkedPaymentAccountId = optionalStr(billingRaw, 'linked_payment_account_id', 'linkedPaymentAccountId');

  const bankTransfer = btRaw ? normalizeBankTransfer(btRaw) : undefined;
  const bankTransferOk = bankTransfer && bankTransferHasValues(bankTransfer);

  const billingPayment: BillingPaymentMethodInfo = {};
  if (linkedPaymentAccountId) billingPayment.linkedPaymentAccountId = linkedPaymentAccountId;
  if (bankTransferOk && bankTransfer) billingPayment.bankTransfer = bankTransfer;

  if (!billingPayment.linkedPaymentAccountId && !billingPayment.bankTransfer) return undefined;

  return { billingPayment };
}
